import { FormEvent, useEffect, useState, useCallback, useRef, useContext, useReducer } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { useTracking } from '../../hooks/useTracking';

import Loader from '../../components/Loader';
import FormElements from '../../components/FormElements';
import LayoutPicker from '../../components/LayoutPicker';
import LayoutPickerItem from '../../components/LayoutPicker/Item';
import ContributionMenu from '../../components/ContributionMenu';
import Onboarding from '../../components/Onboarding';
import SiteContextPreview from '../../components/SiteContextPreview';

import * as Type from '../../types';
import * as S from './styled';
import * as Helper from '../../helpers';
import { AppContext } from '../../context/App';
import FooterUniqueModal from '../../components/modals/FooterUniqueModal';
import { StepsContext } from '../../context/Steps';

interface pathParams {
	id?: string;
}

type DataReducerType = (state: any, value: any) => any;

function dataReducer(state: Type.API_Response, payload: {}) {
	return { ...state, ...payload };
}

function Builder() {
	const { isWelcomeDone, footerContribution, dispatchConfig } = useContext(AppContext);
	const { clientId } = useContext(AppContext);
	const { step, hasBackStep, hasNextStep, isLastStep, nextStepEnabled, updateSteps } = useContext(StepsContext);
	const [event] = useTracking();
	const { id } = useParams<pathParams>();
	const [data, updateData] = useReducer<DataReducerType>(dataReducer, Helper.defaultContributionData);
	const [formStatus, setFormStatus] = useState<Type.FormStatus>('disabled');
	const [previewState, setPreviewState] = useState<string>('collapsed');
	const [loaded, setLoaded] = useState<boolean>(false);
	const [onboarding, setOnboarding] = useState<boolean>(false);
	const [redirectPath, setRedirectPath] = useState<string>('');
	const isFirstLoad = useRef(true);
	const isPreviewExpanded: boolean = previewState === 'expanded';
	const isFooterAvailable: boolean = footerContribution === 0 || footerContribution === data.ID;

	const togglePreviewState = () => setPreviewState(isPreviewExpanded ? 'collapsed' : 'expanded');

	const shutOnboarding = useCallback(() => {
		setOnboarding(false);
	}, []);

	function submitForm() {
		if (formStatus === 'submitting') {
			return;
		}

		setFormStatus('submitting');

		Helper.pushContributionToApi(data.ID, {
			name: data.post_title,
			dialog_header: data.dialog_header,
			dialog_content: data.dialog_content,
			all_amounts: data.all_amounts,
			layout_type: data.layout_type,
		}).then(data => {
			if (data.ID) {
				setFormStatus('submitted');

				if (data.layout_type === 'footer') {
					dispatchConfig({
						payload: {
							footerContribution: data.ID,
						},
					});
				}

				event({
					category: 'LP RevGen Contributions',
					action: 'New ShortCode',
					label: `${clientId} - ${data.post_title} - ${data.all_amounts.join(' - ')}`,
				});

				dispatchConfig({ payload: { hasContributions: true } });

				data.layout_type !== 'footer' && Helper.copyShortcodeToClipboard(data.ID);

				setTimeout(() => {
					setRedirectPath('/contributions/dashboard');
				}, 2500);
			} else {
				setFormStatus('error');
			}
		});
	}

	function handleOnSubmit(e: FormEvent) {
		e.preventDefault();

		if (formStatus === 'submitting') {
			return;
		}

		if (!clientId.length) {
			setFormStatus('enabled');
			setOnboarding(true);

			return;
		}

		submitForm();
	}

	function getSubmitButtonText() {
		if (formStatus === 'submitting') {
			return 'Saving…';
		} else if (formStatus === 'submitted') {
			return data.layout_type === 'footer' ? 'Done!' : 'Done! Code copied to clipboard.';
		} else {
			if (data.layout_type !== 'footer') {
				return 'Save and Copy Code';
			} else if (data.layout_type === 'footer' && data.ID === 0) {
				return 'Launch';
			} else {
				return 'Update';
			}
		}
	}

	useEffect(() => {
		if (typeof id !== 'undefined') {
			setLoaded(false);
			updateData({ ID: parseInt(id as string) });
		} else {
			updateData({ ID: 0 });
		}
	}, [id]);

	useEffect(() => {
		if (data.ID === -1) {
			return;
		}

		if (data.ID === 0) {
			setLoaded(true);

			return;
		}

		Helper.getContributionFromApi(data.ID).then(data => {
			updateData(data);
			updateSteps({ step: 2 });
			setLoaded(true);
		});
	}, [data.ID, updateData, updateSteps]);

	useEffect(() => {
		if (!loaded) {
			return;
		}

		if (isFirstLoad.current) {
			isFirstLoad.current = false;

			return;
		}

		if (!data.post_title) {
			setFormStatus('disabled');
		} else {
			setFormStatus('enabled');
		}

		if (data.layout_type !== 'footer') {
			updateData({ dialogContent: '' });
		}
	}, [data.layout_type, data.dialog_header, data.dialogContent, data.all_amounts, data.post_title, loaded]);

	useEffect(() => {
		if (step === 1) {
			const isNextStepAvailable = data.layout_type !== 'footer' || isFooterAvailable;
			updateSteps({ nextStepEnabled: isNextStepAvailable });
		}
	}, [step, isFooterAvailable, updateSteps, data.layout_type]);

	return (
		<>
			{!isWelcomeDone && <Redirect to={Helper.appPaths.welcome} />}

			<Loader enabled={!loaded} />
			<S.GlobalStyle />

			{redirectPath !== '' && <Redirect to={redirectPath} />}
			{data.layout_type === 'footer' && !isFooterAvailable && <FooterUniqueModal />}

			<S.BuilderContainer previewState={previewState}>
				<S.FormContainer>
					{loaded && (
						<S.Form onSubmit={handleOnSubmit}>
							<S.FormTitle>{step === 1 ? 'Create Your Contribution' : 'Customize Your Contribution'}</S.FormTitle>

							{step === 1 && (
								<LayoutPicker>
									{Helper.availableLayouts.map((layout, index) => (
										<LayoutPickerItem
											key={index}
											type={layout}
											layout={data.layout_type}
											onLayoutChange={(value: Type.LayoutType) => updateData({ layout_type: value })}
											isValid={layout !== 'footer' || isFooterAvailable}
										/>
									))}
								</LayoutPicker>
							)}

							{step === 2 && (
								<FormElements
									formStatus={formStatus}
									campaignName={data.post_title}
									onCampaignNameChange={(value: string) => updateData({ post_title: value })}
									dialogHeader={data.dialog_header}
									onDialogHeaderChange={(value: string) => updateData({ dialog_header: value })}
									dialogContent={data.dialog_content}
									onDialogContentChange={(value: string) => updateData({ dialog_content: value })}
									amounts={data.all_amounts}
									onAmountsChange={newAmounts => updateData({ all_amounts: newAmounts })}
									layoutType={data.layout_type}
								/>
							)}

							<S.FormButtons>
								{hasBackStep && (
									<S.Button type="button" onClick={() => updateSteps({ step: 'back' })}>
										Back
									</S.Button>
								)}
								{hasNextStep && (
									<S.Button
										type="button"
										primary
										onClick={() => updateSteps({ step: 'next' })}
										disabled={!nextStepEnabled}
									>
										Continue
									</S.Button>
								)}
								{isLastStep && (
									<S.Button primary type="submit" disabled={formStatus === 'disabled'}>
										{getSubmitButtonText()}
									</S.Button>
								)}
							</S.FormButtons>
						</S.Form>
					)}
				</S.FormContainer>
				<S.PreviewContainer>
					<S.ToggleButton onClick={togglePreviewState} previewState={previewState}>
						{isPreviewExpanded ? 'Collapse preview' : 'Expand preview'}
					</S.ToggleButton>

					<S.Preview>
						{step === 1 ? (
							<SiteContextPreview layout={data.layout_type} />
						) : (
							<ContributionMenu
								layout={data.layout_type}
								amounts={data.all_amounts}
								dialogHeader={data.dialog_header}
								dialogContent={data.dialog_content}
								spacious={!isPreviewExpanded}
							/>
						)}
					</S.Preview>
				</S.PreviewContainer>
			</S.BuilderContainer>

			<Onboarding active={onboarding} onComplete={submitForm} onCancel={shutOnboarding} />
		</>
	);
}

export default Builder;
